// =====================================================================
// Rapprochements
// =====================================================================
// Yambi détecte qu'un ancêtre déclaré de deux côtés désigne probablement
// la même personne, et met les deux comptes en contact.
//
// Pendant l'échange, l'identité de l'autre est MASQUÉE : pseudonyme, pas
// de nom, pas de photo, pas de village, et le nom de l'ancêtre commun
// n'est pas affiché non plus. Dix messages chacun pour se reconnaître.
// Les deux doivent confirmer. C'est à ce moment-là, et pas avant, que
// les noms apparaissent et que les deux familles se relient.
// =====================================================================

import { useEffect, useRef, useState } from 'react';
import { api, ErreurApi } from '../api';
import type { Message, Rapprochement } from '../../partage/types';
import {
  Alerte,
  Chargement,
  IconeLiens,
  IconeMasque,
  Vide,
  nomComplet,
} from '../composants/base';

export function Rapprochements({ onChangement }: { onChangement?: () => void }) {
  const [liste, setListe] = useState<Rapprochement[] | null>(null);
  const [ouvert, setOuvert] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  async function charger() {
    try {
      setListe(await api.rapprochements());
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Chargement impossible.');
    }
  }

  useEffect(() => {
    void charger();
  }, []);

  if (erreur) return <Alerte>{erreur}</Alerte>;
  if (liste === null) return <Chargement />;

  const actif = liste.find((r) => r.id === ouvert);
  if (actif) {
    return (
      <Conversation
        rapprochement={actif}
        onFermer={() => setOuvert(null)}
        onChangement={() => {
          void charger();
          onChangement?.();
        }}
      />
    );
  }

  const enCours = liste.filter((r) => r.statut !== 'confirme');
  const confirmes = liste.filter((r) => r.statut === 'confirme');

  return (
    <>
      <div className="carte accent">
        <h2>Comment ça marche</h2>
        <p className="doux">
          Quand quelqu'un déclare le même aïeul que vous, Yambi vous met en
          contact sans dire qui vous êtes. Vous avez dix messages chacun pour
          vous reconnaître. Si vous confirmez tous les deux, vos familles se
          relient.
        </p>
      </div>

      {enCours.length === 0 && confirmes.length === 0 && (
        <Vide icone={<IconeLiens />}>
          Aucun rapprochement pour l'instant. Plus vous complétez votre arbre,
          plus Yambi a de chances de retrouver votre famille.
        </Vide>
      )}

      {enCours.length > 0 && (
        <section>
          <h3>À vérifier</h3>
          {enCours.map((r) => (
            <CarteRapprochement key={r.id} r={r} onOuvrir={() => setOuvert(r.id)} />
          ))}
        </section>
      )}

      {confirmes.length > 0 && (
        <section>
          <h3>Liens établis</h3>
          {confirmes.map((r) => (
            <CarteRapprochement key={r.id} r={r} onOuvrir={() => setOuvert(r.id)} />
          ))}
        </section>
      )}
    </>
  );
}

function CarteRapprochement({
  r,
  onOuvrir,
}: {
  r: Rapprochement;
  onOuvrir: () => void;
}) {
  const confirme = r.statut === 'confirme';
  const generation = r.generation === 1 ? 'un parent' : 'un grand-parent';

  return (
    <div className="carte">
      <div className="masque-entete">
        <div className="masque-jeton" aria-hidden="true">
          {confirme ? (
            <span style={{ fontWeight: 650, color: 'var(--accent)' }}>
              {(r.prenoms_autre ?? r.nom_autre ?? '?').slice(0, 1).toUpperCase()}
            </span>
          ) : (
            <IconeMasque />
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 580 }}>
            {confirme
              ? nomComplet(r.nom_autre, r.prenoms_autre)
              : r.pseudonyme_autre}
          </div>
          <div className="doux">
            Lien possible par {generation}
          </div>
        </div>
        <span className={`etiquette${confirme ? ' confirme' : ''}`}>
          {confirme
            ? 'confirmé'
            : r.j_ai_confirme
              ? 'en attente'
              : r.statut === 'conversation'
                ? 'échange'
                : 'nouveau'}
        </span>
      </div>

      {confirme && r.nom_personne_commune && (
        <p className="doux">
          Ancêtre commun :{' '}
          <strong style={{ color: 'var(--texte)' }}>
            {nomComplet(r.nom_personne_commune, r.prenoms_personne_commune)}
          </strong>
        </p>
      )}

      {!confirme && r.j_ai_confirme && (
        <p className="doux">
          Vous avez confirmé. En attente de l'autre personne.
        </p>
      )}

      <button className="bouton secondaire mince" onClick={onOuvrir}>
        {confirme ? 'Voir la conversation' : 'Ouvrir'}
      </button>
    </div>
  );
}

// =====================================================================
// La fenêtre d'échange
// =====================================================================

function Conversation({
  rapprochement: r,
  onFermer,
  onChangement,
}: {
  rapprochement: Rapprochement;
  onFermer: () => void;
  onChangement: () => void;
}) {
  const [messages, setMessages] = useState<Message[] | null>(null);
  const [brouillon, setBrouillon] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [restants, setRestants] = useState(r.messages_restants);
  const [signalement, setSignalement] = useState<string | null>(null);
  const finDuFil = useRef<HTMLDivElement>(null);

  const confirme = r.statut === 'confirme';

  async function charger() {
    try {
      setMessages(await api.messages(r.id));
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Chargement impossible.');
    }
  }

  useEffect(() => {
    void charger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r.id]);

  useEffect(() => {
    finDuFil.current?.scrollIntoView({ block: 'end' });
  }, [messages]);

  async function envoyer(e: React.FormEvent) {
    e.preventDefault();
    const corps = brouillon.trim();
    if (!corps) return;

    setErreur(null);
    setEnvoi(true);
    try {
      await api.envoyerMessage(r.id, corps);
      setBrouillon('');
      setRestants((n) => Math.max(0, n - 1));
      await charger();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Envoi impossible.');
    } finally {
      setEnvoi(false);
    }
  }

  async function confirmer() {
    setErreur(null);
    try {
      await api.confirmerRapprochement(r.id);
      onChangement();
      onFermer();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Action impossible.');
    }
  }

  async function envoyerSignalement() {
    if (!signalement?.trim()) return;
    setErreur(null);
    try {
      await api.signaler(r.id, signalement.trim());
      onChangement();
      onFermer();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Action impossible.');
    }
  }

  async function refuser() {
    setErreur(null);
    try {
      await api.refuserRapprochement(r.id);
      onChangement();
      onFermer();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Action impossible.');
    }
  }

  return (
    <>
      <button className="bouton discret retour" onClick={onFermer}>
        ← Retour
      </button>

      <div className="carte">
        <div className="masque-entete">
          <div className="masque-jeton" aria-hidden="true">
            <IconeMasque />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 580 }}>
              {confirme
                ? nomComplet(r.nom_autre, r.prenoms_autre)
                : r.pseudonyme_autre}
            </div>
            <div className="doux">
              {confirme
                ? 'Identité révélée'
                : 'Identité masquée jusqu’à confirmation'}
            </div>
          </div>
        </div>

        {!confirme && (
          <p className="doux">
            Vous ne voyez pas encore qui est cette personne, et elle ne vous
            voit pas non plus. Posez-lui des questions sur votre famille :
            les noms, le village, les anciens. Si vous vous reconnaissez,
            confirmez tous les deux.
          </p>
        )}

        {erreur && <Alerte>{erreur}</Alerte>}

        {messages === null ? (
          <Chargement texte="Chargement de l'échange…" />
        ) : (
          <div className="fil">
            {messages.length === 0 && (
              <p className="doux" style={{ textAlign: 'center', padding: 14 }}>
                Personne n'a encore écrit. À vous.
              </p>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`bulle ${m.de_moi ? 'moi' : 'lui'}`}>
                {m.corps}
              </div>
            ))}
            <div ref={finDuFil} />
          </div>
        )}

        {!confirme && (
          <>
            <form className="composer" onSubmit={envoyer}>
              <textarea
                value={brouillon}
                onChange={(e) => setBrouillon(e.target.value)}
                placeholder={
                  restants > 0 ? 'Votre message…' : 'Plus de messages disponibles'
                }
                rows={1}
                maxLength={1000}
                disabled={restants === 0 || envoi}
                aria-label="Votre message"
              />
              <button
                className="bouton"
                type="submit"
                disabled={!brouillon.trim() || restants === 0 || envoi}
              >
                Envoyer
              </button>
            </form>
            <p className={`compteur${restants <= 2 ? ' bas' : ''}`}>
              {restants === 0
                ? 'Vous avez utilisé vos 10 messages.'
                : `${restants} message${restants > 1 ? 's' : ''} restant${restants > 1 ? 's' : ''}`}
            </p>
          </>
        )}
      </div>

      {!confirme && signalement !== null && (
        <div className="carte">
          <h2>Signaler cette personne</h2>
          <p className="doux" style={{ marginBottom: 13 }}>
            Dites-nous ce qui s'est passé. Ce signalement nous est transmis,
            et la personne est <strong>bloquée définitivement</strong> : elle
            ne vous sera plus jamais proposée, dans aucun sens.
          </p>
          <div className="champ">
            <label htmlFor="motif">Ce qui s'est passé</label>
            <textarea
              id="motif"
              rows={3}
              maxLength={2000}
              value={signalement}
              onChange={(e) => setSignalement(e.target.value)}
              placeholder="Demande d'argent, usurpation d'identité, propos déplacés…"
            />
          </div>
          <div className="rangee-boutons">
            <button
              className="bouton secondaire"
              onClick={() => setSignalement(null)}
            >
              Annuler
            </button>
            <button
              className="bouton danger"
              onClick={envoyerSignalement}
              disabled={signalement.trim().length < 3}
            >
              Signaler et bloquer
            </button>
          </div>
        </div>
      )}

      {!confirme && signalement === null && (
        <div className="carte">
          <h2>Est-ce bien votre famille ?</h2>
          <p className="doux" style={{ marginBottom: 14 }}>
            Ne confirmez que si vous êtes sûr. Les deux familles seront reliées
            et vos noms deviendront visibles l'un pour l'autre.
          </p>
          <div className="rangee-boutons">
            <button className="bouton danger" onClick={refuser}>
              Ce n'est pas ma famille
            </button>
            <button
              className="bouton"
              onClick={confirmer}
              disabled={r.j_ai_confirme}
            >
              {r.j_ai_confirme ? 'Déjà confirmé' : 'Oui, confirmer'}
            </button>
          </div>

          <button
            className="bouton danger mince"
            style={{ marginTop: 16 }}
            onClick={() => setSignalement('')}
          >
            Signaler cette personne
          </button>
        </div>
      )}
    </>
  );
}
